import React, { useCallback } from "react";
import { Col, Row } from "antd";
import { Form, SubmitButton, Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Formik, FormikProps } from "formik";
import { WorkflowAction, Integration, App } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { Button } from "@heroui/react";

interface FormValues {
  integrationId?: number;
}

/**
 * CycleCounts component renders a form for cycle counts configuration.
 *
 * @param {Object} props - The properties object.
 * @param {App} props.app - The application instance.
 * @param {WorkflowAction} props.action - The workflow action instance.
 * @param {Function} props.addWorkflowAction - Function to add a workflow action.
 * @param {Integration[]} props.integrations - Array of integrations.
 * @param {Function} props.removeWorkflowAction - Function to remove a workflow action.
 * @param {Function} props.updateAction - Function to update a workflow action.
 * @param {boolean} props.isNew - Indicates if the action is new.
 * @param {Function} props.resetSelectedApp - Function to reset the selected application.
 * @returns {JSX.Element} The rendered cycle counts component.
 */
export default function CycleCounts({
  app,
  action,
  addWorkflowAction,
  integrations,
  removeWorkflowAction,
  updateAction,
  isNew,
  resetSelectedApp,
}: {
  app: App;
  action: WorkflowAction;
  addWorkflowAction: (action: WorkflowAction) => Promise<any>;
  integrations: Integration[];
  removeWorkflowAction: (action: WorkflowAction) => Promise<any> | undefined;
  updateAction: (action: WorkflowAction) => Promise<any>;
  isNew: boolean;
  resetSelectedApp: () => void;
}) {
  const { t } = useTranslation();

  const removeWorkflowHandler = useCallback(() => {
    removeWorkflowAction(action);
  }, [action, removeWorkflowAction]);

  const addUpdateWorkflowHandler = useCallback(
    (values, actions) => {
      if (isNew) {
        const newAction = { ...values, appId: app.id } as WorkflowAction;
        addWorkflowAction(newAction).then((response) => {
          if (!response?.errors) {
            actions.resetForm();
            resetSelectedApp();
          }
        });
      } else {
        if (action?.id) {
          updateAction({ id: action.id, ...values }).then((response) => {
            if (!response?.errors) {
              actions.resetForm();
            }
          });
        }
      }
    },
    [action, addWorkflowAction, app.id, isNew, resetSelectedApp, updateAction],
  );

  const cycleCountsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form>
          <Row justify="start" gutter={16}>
            <Col span={3}>
              <span className="blockText">{t("translation:run")}</span>
            </Col>
            <Col span={15}>
              <Form.Item name="integrationId">
                <Select
                  name="integrationId"
                  size="large"
                  placeholder={t("translation:select_integration")}
                >
                  {integrations.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col flex="auto">
              <SubmitButton type="primary" size="large" block disabled={!dirty}>
                {isNew ? t("translation:add_action") : t("translation:save")}
              </SubmitButton>
            </Col>
            {!isNew && (
              <Col span={3}>
                <Button
                  size="md"
                  radius="none"
                  variant="bordered"
                  color="default"
                  fullWidth
                  onPress={removeWorkflowHandler}
                  className="hover:border-primary-default hover:text-primary-default bg-white"
                >
                  {t("translation:remove")}
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      ),
      [integrations, isNew, removeWorkflowHandler, t],
    );

  const initialValues: FormValues = {
    integrationId: undefined,
  };
  return (
    <Row justify="start" gutter={16}>
      <Col span={24}>
        <Formik
          enableReinitialize
          component={cycleCountsForm}
          initialValues={{
            ...(isNew
              ? initialValues
              : { integrationId: action.integrationId }),
          }}
          validationSchema={simpleSchemaBuilder([
            { name: "integrationId", type: "number", required: true },
          ])}
          onSubmit={addUpdateWorkflowHandler}
        />
      </Col>
    </Row>
  );
}
